import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Video, Trash2, Plus, ShieldCheck, Users, BookOpen, Loader2, GraduationCap, Pencil, Tag, Bell, FolderOpen, Type, Mail, ClipboardCheck, Rocket, Gamepad2 } from 'lucide-react';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api, { API_BASE } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { AutoTextarea } from '../components/ui/auto-textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import EditDialog from '../components/EditDialog';
import { fmtDuration } from '../lib/curriculum';
import PlansManager from '../components/PlansManager';
import UsersManager from '../components/UsersManager';
import RevenueCard from '../components/RevenueCard';
import RequestsManager from '../components/RequestsManager';
import { PendingRequestsAlert } from '../components/PendingRequestsAlert';
import CategoriesManager from '../components/CategoriesManager';
import SiteTextsManager from '../components/SiteTextsManager';
import NewsletterManager from '../components/NewsletterManager';
import SimulationsManager from '../components/SimulationsManager';
import MultiCategorySelect from '../components/MultiCategorySelect';
import TranslateButton from '../components/TranslateButton';
import ColorPicker from '../components/ColorPicker';
import { RichTextEditor } from '../components/RichTextEditor';
import { stripHtml } from '../lib/richText';
import { FreeToggle } from '../components/FreeToggle';
import { useCategories } from '../hooks/useCategories';
import { useToast } from '../hooks/use-toast';
import PublishManager from '../components/publish/PublishManager';
import { LiveLock } from '../components/publish/LiveLock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { lang, isRTL } = useLang();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { categories: VIDEO_CATEGORIES, getLabel: getCategoryLabel } = useCategories();

  const [videos, setVideos] = useState([]);
  const [isPreview, setIsPreview] = useState(null);
  useEffect(() => { api.get('/publish/env').then(r => setIsPreview(r.data.is_preview)).catch(() => setIsPreview(false)); }, []);
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get('tab') || 'videos');
  const [pendingRequests, setPendingRequests] = useState(0);
  const loadPendingRequests = useCallback(() => {
    api.get('/admin/requests', { params: { status: 'pending' } }).then(({ data }) => setPendingRequests(data.length)).catch(() => {});
  }, []);
  useEffect(() => { loadPendingRequests(); }, [loadPendingRequests]);
  const [posts, setPosts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // { type, item }

  const [videoForm, setVideoForm] = useState({ titleAr: '', titleEn: '', descAr: '', descEn: '', categories: ['general'], file: null, is_free: false });
  const [postForm, setPostForm] = useState({ titleAr: '', titleEn: '', contentAr: '', contentEn: '', categories: ['general'], is_free: false, image: null, imagePreview: '', coverKind: '', sections: [] });
  const [courseForm, setCourseForm] = useState({
    titleAr: '', titleEn: '', brandAr: '', brandEn: '',
    subtitleAr: '', subtitleEn: '', levelAr: '', levelEn: '',
    descriptionAr: '', descriptionEn: '', hours: 0,
    color: 'blue', youtube: '',
    categories: ['general'],
    is_free: false,
    image: null, imagePreview: '',
    videoFile: null, videoFileName: '',
  });

  const L = (ar, en) => tx(lang, ar, en);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchAll();
  }, [user, authLoading, navigate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [v, p, c] = await Promise.all([api.get('/videos'), api.get('/posts'), api.get('/courses')]);
      setVideos(v.data || []);
      setPosts(p.data || []);
      setCourses(c.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load' });
    } finally {
      setLoading(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setVideoForm({ ...videoForm, file: f });
  };

  const addVideo = async (e) => {
    e.preventDefault();
    if (!videoForm.file) {
      toast({ title: L('اختر ملف فيديو', 'Choose a video file') });
      return;
    }
    setBusy(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('titleAr', videoForm.titleAr);
      fd.append('titleEn', videoForm.titleEn);
      fd.append('descAr', videoForm.descAr);
      fd.append('descEn', videoForm.descEn);
      fd.append('categories', (videoForm.categories || ['general']).join(','));
      fd.append('is_free', videoForm.is_free ? 'true' : 'false');
      fd.append('file', videoForm.file);
      const { data } = await api.post('/videos', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => {
          if (ev.total) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      setVideos([data, ...videos]);
      setVideoForm({ titleAr: '', titleEn: '', descAr: '', descEn: '', categories: ['general'], file: null, is_free: false });
      toast({ title: L('تم رفع الفيديو', 'Video uploaded') });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Upload failed' });
    } finally {
      setBusy(false);
      setUploadProgress(0);
    }
  };

  const addPost = async (e) => {
    e.preventDefault();
    if (!stripHtml(postForm.contentAr) && !/<img/i.test(postForm.contentAr || '')) {
      toast({ title: L('اكتب محتوى المنشور بالعربية', 'Please write the Arabic content') });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('titleAr', postForm.titleAr);
      fd.append('titleEn', postForm.titleEn);
      fd.append('contentAr', postForm.contentAr);
      fd.append('contentEn', postForm.contentEn);
      fd.append('categories', (postForm.categories || ['general']).join(','));
      fd.append('is_free', postForm.is_free ? 'true' : 'false');
      if (postForm.image) {
        const fieldName = postForm.coverKind === 'video' ? 'video' : 'image';
        fd.append(fieldName, postForm.image);
      }
      const { data } = await api.post('/posts', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Upload any locally-staged sections in order after the post exists
      const localSections = postForm.sections || [];
      let finalPost = data;
      for (let i = 0; i < localSections.length; i += 1) {
        const s = localSections[i];
        const sfd = new FormData();
        sfd.append('type', s.type);
        sfd.append('order', String(i));
        sfd.append('textAr', s.textAr || '');
        sfd.append('textEn', s.textEn || '');
        sfd.append('captionAr', s.captionAr || '');
        sfd.append('captionEn', s.captionEn || '');
        if (s.file) sfd.append('file', s.file);
        try {
          // eslint-disable-next-line no-await-in-loop
          const { data: updated } = await api.post(`/posts/${data.id}/sections`, sfd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          finalPost = updated;
        } catch (_) { /* ignore */ }
      }

      setPosts([finalPost, ...posts]);
      setPostForm({ titleAr: '', titleEn: '', contentAr: '', contentEn: '', categories: ['general'], is_free: false, image: null, imagePreview: '', coverKind: '', sections: [] });

      const totalSec = localSections.length;
      const msg = totalSec > 0
        ? L(`تم نشر المنشور مع ${totalSec} قسم`, `Post created with ${totalSec} section(s)`)
        : L('تم نشر المنشور', 'Post created');
      toast({ title: msg });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  const onCoverFile = (e, kind) => {
    const f = e.target.files?.[0];
    if (f) setPostForm({ ...postForm, image: f, imagePreview: URL.createObjectURL(f), coverKind: kind });
  };

  const toggleFree = async (kind, item) => {
    const next = !item.is_free;
    const setters = { video: setVideos, post: setPosts, course: setCourses };
    const paths = { video: 'videos', post: 'posts', course: 'courses' };
    setters[kind](list => list.map(x => (x.id === item.id ? { ...x, is_free: next } : x)));
    try {
      const fd = new FormData();
      fd.append('is_free', next ? 'true' : 'false');
      await api.patch(`/${paths[kind]}/${item.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: next ? L('أصبح مجانياً', 'Now free') : L('أصبح مدفوعاً', 'Now paid') });
    } catch (e) {
      setters[kind](list => list.map(x => (x.id === item.id ? { ...x, is_free: !next } : x)));
      toast({ title: 'Error', description: e?.response?.data?.detail || L('تعذر الحفظ', 'Could not save') });
    }
  };

  const removeVideo = async (id) => {
    try {
      await api.delete(`/videos/${id}`);
      setVideos(videos.filter(v => v.id !== id));
      toast({ title: L('تم الحذف', 'Deleted') });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete' });
    }
  };

  const removePost = async (id) => {
    try {
      await api.delete(`/posts/${id}`);
      setPosts(posts.filter(p => p.id !== id));
      toast({ title: L('تم الحذف', 'Deleted') });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete' });
    }
  };

  const onCourseImage = (e) => {
    const f = e.target.files?.[0];
    if (f) setCourseForm({ ...courseForm, image: f, imagePreview: URL.createObjectURL(f) });
  };

  const onCourseVideo = (e) => {
    const f = e.target.files?.[0];
    if (f) setCourseForm({ ...courseForm, videoFile: f, videoFileName: f.name });
  };

  const addCourse = async (e) => {
    e.preventDefault();
    setBusy(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      Object.entries(courseForm).forEach(([k, v]) => {
        if (['image', 'imagePreview', 'videoFile', 'videoFileName', 'categories'].includes(k)) return;
        fd.append(k, v ?? '');
      });
      fd.append('categories', (courseForm.categories || ['general']).join(','));
      if (courseForm.image) fd.append('image', courseForm.image);
      if (courseForm.videoFile) fd.append('video', courseForm.videoFile);
      const { data } = await api.post('/courses', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => {
          if (ev.total) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      setCourses([data, ...courses]);
      setCourseForm({
        titleAr: '', titleEn: '', brandAr: '', brandEn: '',
        subtitleAr: '', subtitleEn: '', levelAr: '', levelEn: '',
        descriptionAr: '', descriptionEn: '', hours: 0,
        color: 'blue', youtube: '',
        categories: ['general'],
        image: null, imagePreview: '',
        videoFile: null, videoFileName: '',
        is_free: false,
      });
      toast({ title: L('تم إضافة الكورس', 'Course added') });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed' });
    } finally {
      setBusy(false);
      setUploadProgress(0);
    }
  };

  const removeCourse = async (id) => {
    try {
      await api.delete(`/courses/${id}`);
      setCourses(courses.filter(c => c.id !== id));
      toast({ title: L('تم الحذف', 'Deleted') });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete' });
    }
  };

  if (authLoading || !user) return null;

  return (
    <main className="page-y min-h-screen">
      <div className="site-container">
        <div className={`rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-200 p-6 mb-6 flex items-center gap-4 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
          <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center"><ShieldCheck className="w-7 h-7 text-blue-700" /></div>
          <div className="flex-1">
            <h1 className="text-slate-900 text-2xl font-extrabold">{L('لوحة تحكم الأدمن', 'Admin Dashboard')}</h1>
            <p className="text-slate-600 text-sm">{L('إدارة الفيديوهات والمنشورات — مرحباً ', 'Manage videos and posts — Welcome ')}{user.name}</p>
          </div>
        </div>
        {isPreview && (
          <div data-testid="preview-env-banner" className="mb-6 flex items-center gap-3 rounded-xl border border-blue-300 bg-blue-50 px-4 py-3 text-blue-800 text-sm font-bold">
            <ShieldCheck className="w-5 h-5 shrink-0" />
            {L('أنت في المعاينة — التعديلات هنا لا تظهر للزوار حتى تعتمدها من تبويب «النشر» وتضغط Deploy.', 'Preview — changes stay here until you approve a release in the Publish tab and press Deploy.')}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { Ic: Video, label: L('فيديوهات', 'Videos'), val: videos.length },
            { Ic: FileText, label: L('منشورات', 'Posts'), val: posts.length },
            { Ic: GraduationCap, label: L('كورسات', 'Courses'), val: courses.length },
            { Ic: Users, label: L('مستخدمين', 'Users'), val: '+15K' },
          ].map((s, i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-5 flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center"><s.Ic className="w-6 h-6 text-blue-600" /></div>
              <div>
                <div className="text-2xl font-extrabold text-slate-900">{s.val}</div>
                <div className="text-xs text-slate-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <RevenueCard />

        <PendingRequestsAlert count={pendingRequests} onOpen={() => setTab('requests')} />

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
            <TabsTrigger value="videos" data-testid="tab-videos" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"><Video className="w-4 h-4 me-2" />{L('الفيديوهات', 'Videos')}</TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"><FileText className="w-4 h-4 me-2" />{L('المنشورات', 'Posts')}</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"><GraduationCap className="w-4 h-4 me-2" />{L('الكورسات', 'Courses')}</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"><Tag className="w-4 h-4 me-2" />{L('الباقات', 'Plans')}</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"><Users className="w-4 h-4 me-2" />{L('المستخدمين', 'Users')}</TabsTrigger>
            <TabsTrigger value="requests" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-requests"><Bell className="w-4 h-4 me-2" />{L('الطلبات', 'Requests')}{pendingRequests > 0 && <span data-testid="tab-requests-badge" className="ms-2 min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold inline-flex items-center justify-center">{pendingRequests}</span>}</TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-categories"><FolderOpen className="w-4 h-4 me-2" />{L('التصنيفات', 'Categories')}</TabsTrigger>
            <TabsTrigger value="site-texts" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-site-texts"><Type className="w-4 h-4 me-2" />{L('نصوص الموقع', 'Site Texts')}</TabsTrigger>
            <TabsTrigger value="simulations" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-simulations"><Gamepad2 className="w-4 h-4 me-2" />{L('المحاكاة', 'Simulations')}</TabsTrigger>
            {isPreview && <TabsTrigger value="publish" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-publish"><Rocket className="w-4 h-4 me-2" />{L('النشر', 'Publish')}</TabsTrigger>}
            <TabsTrigger value="newsletter" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-newsletter"><Mail className="w-4 h-4 me-2" />{L('مشتركو النشرة', 'Newsletter')}</TabsTrigger>
          </TabsList>

          <TabsContent value="videos" className="mt-5">
            <LiveLock locked={isPreview === false}>
            <form onSubmit={addVideo} className="rounded-2xl bg-white border border-slate-200 p-6 mb-6 space-y-4">
              <h3 className="text-slate-900 font-bold text-lg flex items-center gap-2"><Plus className="w-5 h-5 text-blue-600" />{L('رفع فيديو جديد', 'Upload New Video')}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="video-title-ar" required placeholder={L('العنوان بالعربية', 'Title (Arabic)')} value={videoForm.titleAr} onChange={e => setVideoForm({ ...videoForm, titleAr: e.target.value })} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
                <Input data-testid="video-title-en" placeholder={L('العنوان بالإنجليزية', 'Title (English)')} value={videoForm.titleEn} onChange={e => setVideoForm({ ...videoForm, titleEn: e.target.value })} className="bg-slate-50 border-slate-200 text-slate-900 h-11" dir="ltr" />
                <Textarea data-testid="video-desc-ar" placeholder={L('الوصف بالعربية', 'Description (Arabic)')} value={videoForm.descAr} onChange={e => setVideoForm({ ...videoForm, descAr: e.target.value })} rows={3} className="bg-slate-50 border-slate-200 text-slate-900" />
                <Textarea data-testid="video-desc-en" placeholder={L('الوصف بالإنجليزية', 'Description (English)')} value={videoForm.descEn} onChange={e => setVideoForm({ ...videoForm, descEn: e.target.value })} rows={3} className="bg-slate-50 border-slate-200 text-slate-900" dir="ltr" />
              </div>
              <div className="flex flex-wrap gap-2">
                <TranslateButton
                  sourceLang="ar"
                  targetLang="en"
                  getValues={() => videoForm}
                  setValue={(k, v) => setVideoForm(prev => ({ ...prev, [k]: v }))}
                  fields={[
                    { source: 'titleAr', target: 'titleEn' },
                    { source: 'descAr', target: 'descEn' },
                  ]}
                  testId="video-translate-ar-en"
                />
                <TranslateButton
                  sourceLang="en"
                  targetLang="ar"
                  getValues={() => videoForm}
                  setValue={(k, v) => setVideoForm(prev => ({ ...prev, [k]: v }))}
                  fields={[
                    { source: 'titleEn', target: 'titleAr' },
                    { source: 'descEn', target: 'descAr' },
                  ]}
                  testId="video-translate-en-ar"
                />
              </div>
              <div>
                <label className="text-slate-600 text-sm mb-2 block">{L('التصنيفات (يمكن اختيار أكثر من واحد)', 'Categories (multiple allowed)')}</label>
                <MultiCategorySelect
                  value={videoForm.categories}
                  onChange={(arr) => setVideoForm({ ...videoForm, categories: arr })}
                  dataTestId="video-categories-select"
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-slate-600 text-sm">{L('حالة المحتوى:', 'Access:')}</span>
                <FreeToggle value={videoForm.is_free} onChange={(val) => setVideoForm({ ...videoForm, is_free: val })} testId="video-form-free-toggle" />
              </div>
              <label className="block">
                <div className="rounded-xl border-2 border-dashed border-slate-200 hover:border-blue-300 transition-colors p-6 text-center cursor-pointer bg-slate-50">
                  <Upload className="w-7 h-7 text-blue-600 mx-auto mb-2" />
                  <div className="text-slate-700 text-sm font-medium">{videoForm.file?.name || L('اضغط لرفع فيديو', 'Click to upload video')}</div>
                  <div className="text-slate-500 text-xs mt-1">{L('دعم جميع صيغ الفيديو', 'All video formats supported')}</div>
                  <input type="file" accept="video/*" onChange={onFile} className="hidden" />
                </div>
              </label>
              {busy && uploadProgress > 0 && (
                <div className="w-full bg-slate-50 rounded-full h-2 overflow-hidden"><div className="bg-blue-500 h-full transition-all" style={{ width: `${uploadProgress}%` }} /></div>
              )}
              <Button type="submit" disabled={busy} className="btn-primary-blue border-0 h-11 px-7 font-semibold">
                {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
                {L('رفع', 'Upload')}
              </Button>
            </form>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {loading && <div className="col-span-full text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></div>}
              {!loading && videos.length === 0 && <div className="col-span-full text-center text-slate-500 py-10">{L('لا توجد فيديوهات بعد', 'No videos yet')}</div>}
              {videos.map(v => (
                <div key={v.id} className="rounded-xl bg-white border border-slate-200 overflow-hidden">
                  <video src={v.url && v.url.startsWith('/api/') ? `${BACKEND_URL}${v.url}` : v.url} controls controlsList="nodownload" disablePictureInPicture onContextMenu={(e) => e.preventDefault()} className="w-full aspect-video bg-black" />
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-slate-900 font-bold line-clamp-1">{lang === 'ar' ? v.titleAr : v.titleEn}</div>
                      <FreeToggle value={v.is_free} onChange={() => toggleFree('video', v)} testId={`video-free-toggle-${v.id}`} />
                      <span className="shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
                        {getCategoryLabel(v.category || 'general', lang)}
                      </span>
                    </div>
                    <div className="text-slate-500 text-xs mb-3 line-clamp-2">{lang === 'ar' ? v.descAr : v.descEn}</div>
                    <div className="flex gap-2">
                      <Button data-testid={`video-edit-btn-${v.id}`} variant="outline" onClick={() => setEditing({ type: 'video', item: v })} className="flex-1 border-blue-200 text-blue-700 hover:bg-slate-100 h-9"><Pencil className="w-4 h-4 me-1" />{L('تعديل', 'Edit')}</Button>
                      <Button data-testid={`video-delete-btn-${v.id}`} variant="outline" onClick={() => removeVideo(v.id)} className="flex-1 border-red-500/40 text-red-600 hover:bg-red-500/10 hover:text-red-700 h-9"><Trash2 className="w-4 h-4 me-1" />{L('حذف', 'Delete')}</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </LiveLock>
          </TabsContent>

          <TabsContent value="posts" className="mt-5">
            <LiveLock locked={isPreview === false}>
            <form onSubmit={addPost} className="rounded-2xl bg-white border border-slate-200 p-6 mb-6 space-y-4">
              <h3 className="text-slate-900 font-bold text-lg flex items-center gap-2"><Plus className="w-5 h-5 text-blue-600" />{L('إضافة منشور جديد', 'Add New Post')}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="post-title-ar" required placeholder={L('العنوان بالعربية', 'Title (Arabic)')} value={postForm.titleAr} onChange={e => setPostForm({ ...postForm, titleAr: e.target.value })} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
                <Input data-testid="post-title-en" placeholder={L('العنوان بالإنجليزية', 'Title (English)')} value={postForm.titleEn} onChange={e => setPostForm({ ...postForm, titleEn: e.target.value })} className="bg-slate-50 border-slate-200 text-slate-900 h-11" dir="ltr" />
              </div>

              {/* Cover: image OR video */}
              <div>
                <label className="text-slate-600 text-sm mb-2 block">{L('غلاف المنشور — صورة أو فيديو (اختياري)', 'Post cover — image or video (optional)')}</label>
                {postForm.imagePreview ? (
                  <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-black">
                    {postForm.coverKind === 'video' ? (
                      <video src={postForm.imagePreview} controls className="w-full max-h-64" />
                    ) : (
                      <img src={postForm.imagePreview} alt="" className="w-full max-h-64 object-cover" />
                    )}
                    <button type="button" onClick={() => setPostForm({ ...postForm, image: null, imagePreview: '', coverKind: '' })} className="absolute top-2 right-2 bg-red-500/90 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="block">
                      <div className="rounded-xl border-2 border-dashed border-slate-200 hover:border-blue-300 transition-colors p-5 text-center cursor-pointer bg-slate-50" data-testid="post-cover-image-dropzone">
                        <Upload className="w-6 h-6 text-blue-600 mx-auto mb-1.5" />
                        <div className="text-slate-700 text-sm font-medium">{L('اختر صورة غلاف', 'Choose cover image')}</div>
                        <div className="text-slate-500 text-[11px] mt-0.5">JPG, PNG, WEBP</div>
                        <input type="file" accept="image/*" onChange={(e) => onCoverFile(e, 'image')} className="hidden" data-testid="post-cover-image-input" />
                      </div>
                    </label>
                    <label className="block">
                      <div className="rounded-xl border-2 border-dashed border-purple-900/60 hover:border-purple-500/60 transition-colors p-5 text-center cursor-pointer bg-slate-50" data-testid="post-cover-video-dropzone">
                        <Video className="w-6 h-6 text-purple-600 mx-auto mb-1.5" />
                        <div className="text-slate-700 text-sm font-medium">{L('اختر فيديو غلاف', 'Choose cover video')}</div>
                        <div className="text-slate-500 text-[11px] mt-0.5">MP4, MOV, WEBM</div>
                        <input type="file" accept="video/*" onChange={(e) => onCoverFile(e, 'video')} className="hidden" data-testid="post-cover-video-input" />
                      </div>
                    </label>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <RichTextEditor testId="post-content-ar" dir="rtl" minHeight={260} stickyTop={72} value={postForm.contentAr} onChange={v => setPostForm(f => ({ ...f, contentAr: v }))} placeholder={L('المحتوى بالعربية', 'Content (Arabic)')} />
                <RichTextEditor testId="post-content-en" dir="ltr" minHeight={260} stickyTop={72} value={postForm.contentEn} onChange={v => setPostForm(f => ({ ...f, contentEn: v }))} placeholder={L('المحتوى بالإنجليزية', 'Content (English)')} />
              </div>

              <div className="flex flex-wrap gap-2">
                <TranslateButton
                  sourceLang="ar"
                  targetLang="en"
                  getValues={() => postForm}
                  setValue={(k, v) => setPostForm(prev => ({ ...prev, [k]: v }))}
                  fields={[
                    { source: 'titleAr', target: 'titleEn' },
                    { source: 'contentAr', target: 'contentEn' },
                  ]}
                  testId="post-translate-ar-en"
                />
                <TranslateButton
                  sourceLang="en"
                  targetLang="ar"
                  getValues={() => postForm}
                  setValue={(k, v) => setPostForm(prev => ({ ...prev, [k]: v }))}
                  fields={[
                    { source: 'titleEn', target: 'titleAr' },
                    { source: 'contentEn', target: 'contentAr' },
                  ]}
                  testId="post-translate-en-ar"
                />
              </div>

              <div>
                <label className="text-slate-600 text-sm mb-2 block">{L('التصنيفات (يمكن اختيار أكثر من واحد)', 'Categories (multiple allowed)')}</label>
                <MultiCategorySelect
                  value={postForm.categories}
                  onChange={(arr) => setPostForm({ ...postForm, categories: arr })}
                  dataTestId="post-categories-select"
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-slate-600 text-sm">{L('حالة المحتوى:', 'Access:')}</span>
                <FreeToggle value={postForm.is_free} onChange={(val) => setPostForm({ ...postForm, is_free: val })} testId="post-form-free-toggle" />
              </div>

              {/* Ordered content sections editor hidden: inline images now live in the rich editor */}

              <Button type="submit" disabled={busy} className="btn-primary-blue border-0 h-11 px-7 font-semibold" data-testid="post-publish-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
                {L('نشر المنشور', 'Publish post')}
              </Button>
            </form>

            <div className="space-y-3">
              {!loading && posts.length === 0 && <div className="text-center text-slate-500 py-10">{L('لا توجد منشورات بعد', 'No posts yet')}</div>}
              {posts.map(p => (
                <div key={p.id} className="rounded-xl bg-white border border-slate-200 p-5 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><BookOpen className="w-6 h-6 text-blue-600" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <div className="text-slate-900 font-bold">{lang === 'ar' ? p.titleAr : p.titleEn}</div>
                      <FreeToggle value={p.is_free} onChange={() => toggleFree('post', p)} testId={`post-free-toggle-${p.id}`} />
                    </div>
                    <div className="text-slate-500 text-sm mb-3 line-clamp-2">{stripHtml(lang === 'ar' ? p.contentAr : p.contentEn)}</div>
                    <div className="text-xs text-slate-500" dir="ltr">{p.created_at ? new Date(p.created_at).toLocaleString() : ''}</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button data-testid={`post-edit-btn-${p.id}`} variant="outline" onClick={() => setEditing({ type: 'post', item: p })} className="border-blue-200 text-blue-700 hover:bg-slate-100 h-9"><Pencil className="w-4 h-4" /></Button>
                    <Button data-testid={`post-delete-btn-${p.id}`} variant="outline" onClick={() => removePost(p.id)} className="border-red-500/40 text-red-600 hover:bg-red-500/10 hover:text-red-700 h-9"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
            </LiveLock>
          </TabsContent>

          <TabsContent value="courses" className="mt-5">
            <LiveLock locked={isPreview === false}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div>
                <h2 className="text-lg font-extrabold text-slate-900">{L('الكورسات', 'Courses')}</h2>
                <p className="text-sm text-slate-500">{courses.length} {L('كورس', 'courses')}</p>
              </div>
              <Button data-testid="course-add-new-btn" onClick={() => navigate('/admin/courses/new')} className="btn-primary-blue border-0 h-11 px-6 font-bold"><Plus className="w-4 h-4 me-1.5" />{L('إضافة كورس جديد', 'Add new course')}</Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {!loading && courses.length === 0 && <div className="col-span-full text-center text-slate-500 py-10">{L('لا توجد كورسات بعد', 'No courses yet')}</div>}
              {courses.map(c => (
                <div key={c.id} className="rounded-xl bg-white border border-slate-200 overflow-hidden">
                  {c.cover && (
                    <div className="aspect-video bg-black overflow-hidden">
                      <img src={c.cover.startsWith('/api/') ? `${BACKEND_URL}${c.cover}` : c.cover} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <span className="text-xs text-blue-600">{lang === 'ar' ? c.brandAr : c.brandEn}</span>
                      {c.published === false && <span data-testid={`course-draft-badge-${c.id}`} className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-bold">{L('مسودة', 'Draft')}</span>}
                      {c.featured && <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[11px] font-bold">{L('مميز', 'Featured')}</span>}
                    </div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-slate-900 font-bold">{lang === 'ar' ? c.titleAr : c.titleEn}</div>
                      <FreeToggle value={c.is_free} onChange={() => toggleFree('course', c)} testId={`course-free-toggle-${c.id}`} />
                    </div>
                    <div className="text-slate-500 text-xs mb-3 line-clamp-2">{lang === 'ar' ? c.subtitleAr : c.subtitleEn}</div>
                    <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-3">
                      {fmtDuration(c.total_seconds, lang) && <span data-testid={`admin-course-duration-${c.id}`}>{fmtDuration(c.total_seconds, lang)}</span>}
                      <span>{lang === 'ar' ? c.levelAr : c.levelEn}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid={`course-edit-btn-${c.id}`} variant="outline" onClick={() => navigate(`/admin/courses/${c.id}`)} className="flex-1 border-blue-200 text-blue-700 hover:bg-slate-100 h-9"><Pencil className="w-4 h-4 me-1" />{L('تعديل', 'Edit')}</Button>
                      <Button data-testid={`course-delete-btn-${c.id}`} variant="outline" onClick={() => removeCourse(c.id)} className="flex-1 border-red-500/40 text-red-600 hover:bg-red-500/10 hover:text-red-700 h-9"><Trash2 className="w-4 h-4 me-1" />{L('حذف', 'Delete')}</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </LiveLock>
          </TabsContent>

          <TabsContent value="plans" className="mt-5">
            <LiveLock locked={isPreview === false}><PlansManager /></LiveLock>
          </TabsContent>

          <TabsContent value="users" className="mt-5">
            <UsersManager />
          </TabsContent>

          <TabsContent value="requests" className="mt-5">
            <RequestsManager onChanged={loadPendingRequests} />
          </TabsContent>

          <TabsContent value="categories" className="mt-5">
            <LiveLock locked={isPreview === false}><CategoriesManager /></LiveLock>
          </TabsContent>

          <TabsContent value="site-texts" className="mt-5">
            <LiveLock locked={isPreview === false}><SiteTextsManager /></LiveLock>
          </TabsContent>

          <TabsContent value="simulations" className="mt-5">
            <LiveLock locked={isPreview === false}><SimulationsManager /></LiveLock>
          </TabsContent>

          {isPreview && (
            <TabsContent value="publish" className="mt-5">
              <PublishManager />
            </TabsContent>
          )}

          <TabsContent value="newsletter" className="mt-5">
            <NewsletterManager />
          </TabsContent>
        </Tabs>
      </div>

      {editing && (
        <EditDialog
          type={editing.type}
          item={editing.item}
          open={true}
          onClose={() => setEditing(null)}
          onUpdated={(data) => {
            if (editing.type === 'video') setVideos(videos.map(x => x.id === data.id ? data : x));
            if (editing.type === 'post') setPosts(posts.map(x => x.id === data.id ? data : x));
            if (editing.type === 'course') setCourses(courses.map(x => x.id === data.id ? data : x));
          }}
        />
      )}
    </main>
  );
};

export default AdminDashboard;
